/**
 * Google Analytics 4 Service
 * DSGVO- und TTDSG-konforme Implementation
 * 
 * Tracking wird NUR nach expliziter Einwilligung aktiviert.
 * IP-Anonymisierung ist in GA4 standardmäßig aktiviert.
 */

import { logger } from '@/lib/logger';

// GA4 Measurement ID - hier eintragen oder als Umgebungsvariable setzen
const GA_MEASUREMENT_ID = 'G-1V83HG0SQ0';

declare global {
  interface Window {
    gtag: (...args: unknown[]) => void;
    dataLayer: unknown[];
  }
}

let isInitialized = false;
let isDisabled = false;

/**
 * Lädt das GA4 Script dynamisch
 */
const loadGtagScript = (): Promise<void> => {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[src*="googletagmanager.com/gtag"]`)) {
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load GA4 script'));
    document.head.appendChild(script);
  });
};

/**
 * Initialisiert Google Analytics 4
 * Wird NUR aufgerufen wenn Consent vorliegt
 */
export const initializeGA4 = async (): Promise<void> => {
  if (isInitialized || isDisabled) return;
  if (!GA_MEASUREMENT_ID || GA_MEASUREMENT_ID.startsWith('G-XXXX')) {
    logger.warn('GA4: Measurement ID nicht konfiguriert');
    return;
  }

  try {
    await loadGtagScript();

    window.dataLayer = window.dataLayer || [];
    window.gtag = function gtag() {
      window.dataLayer.push(arguments);
    };

    window.gtag('js', new Date());
    
    // Konfiguration mit Datenschutz-Einstellungen
    window.gtag('config', GA_MEASUREMENT_ID, {
      // IP-Anonymisierung (Standard in GA4, aber explizit setzen)
      anonymize_ip: true,
      // Keine personenbezogenen Daten
      allow_google_signals: false,
      allow_ad_personalization_signals: false,
      // Keine User-ID Übertragung
      send_user_id: false,
      // Cookie-Einstellungen
      cookie_flags: 'SameSite=Strict;Secure',
      // Consent Mode v2
      ads_data_redaction: true,
    });

    // Google Consent Mode v2 - Signalisiert Consent-Status
    window.gtag('consent', 'update', {
      analytics_storage: 'granted',
      ad_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied',
    });

    isInitialized = true;
    logger.log('GA4: Erfolgreich initialisiert');
  } catch (error) {
    logger.error('GA4: Initialisierung fehlgeschlagen', error);
  }
};

/**
 * Deaktiviert Google Analytics und löscht Cookies
 * Wird bei Widerruf der Einwilligung aufgerufen
 */
export const disableGA4 = (): void => {
  isDisabled = true;
  isInitialized = false;

  // GA4 Opt-Out setzen
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (window as any)[`ga-disable-${GA_MEASUREMENT_ID}`] = true;

  // Consent Mode aktualisieren
  if (window.gtag) {
    window.gtag('consent', 'update', {
      analytics_storage: 'denied',
      ad_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied',
    });
  }

  // GA4 Cookies löschen
  deleteGA4Cookies();

  logger.log('GA4: Deaktiviert und Cookies gelöscht');
};

/**
 * Löscht alle Google Analytics Cookies
 */
export const deleteGA4Cookies = (): void => {
  const cookies = document.cookie.split(';');
  
  for (const cookie of cookies) {
    const cookieName = cookie.split('=')[0].trim();
    
    // GA4 Cookie-Namen beginnen mit _ga
    if (cookieName.startsWith('_ga')) {
      // Cookie für verschiedene Domains löschen
      const domains = [
        window.location.hostname,
        '.' + window.location.hostname,
        window.location.hostname.split('.').slice(-2).join('.'),
        '.' + window.location.hostname.split('.').slice(-2).join('.'),
      ];

      for (const domain of domains) {
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${domain}`;
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
      }
    }
  }
};

/**
 * Trackt einen Seitenaufruf
 */
export const trackPageView = (pagePath?: string): void => {
  if (!isInitialized || isDisabled) return;
  
  window.gtag('event', 'page_view', {
    page_path: pagePath || window.location.pathname,
    page_title: document.title,
  });
};

/**
 * Trackt ein benutzerdefiniertes Event
 */
export const trackEvent = (
  eventName: string,
  eventParams?: Record<string, unknown>
): void => {
  if (!isInitialized || isDisabled) return;
  
  window.gtag('event', eventName, eventParams);
};

/**
 * Prüft ob GA4 initialisiert ist
 */
export const isGA4Initialized = (): boolean => {
  return isInitialized && !isDisabled;
};

/**
 * Reaktiviert GA4 nach vorherigem Disable
 */
export const enableGA4 = async (): Promise<void> => {
  isDisabled = false;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (window as any)[`ga-disable-${GA_MEASUREMENT_ID}`] = false;
  await initializeGA4();
};
